import React, { useEffect, useRef, useState } from "react";
import { Headphones } from "@phosphor-icons/react";

// "Mini Player" handoff button — sits on every TV stage
// (InAppVideoPlayer, InAppMoviePlayer, AudioVisualizerPlayer). Tap
// hands off the currently-playing media to the floating MiniPlayer
// (audio keeps going) and dismisses the full-screen stage so the
// user can navigate freely to other rooms / pages.
//
// Updated Feb 2026 — user feedback: "the mini player button should
// not be on the TV screen". The pill itself keeps its labeled style,
// but the parents now mount it OUTSIDE the video frame (in the title
// row below the player) so it never overlaps the picture.
//
// First-time coach mark
// ─────────────────────
// We surface a small floating tooltip ("Tap to keep listening while
// you wander") above the pill ONCE per device. The flag is keyed off
// of `hw:bg-pill:coach-seen` in localStorage. Auto-hides after 4.5s
// or when the user taps the pill.
const COACH_LS_KEY = "hw:bg-pill:coach-seen";

export const BackgroundPlayPill = ({ onClick, testid, className = "" }) => {
  const [showCoach, setShowCoach] = useState(false);
  const coachTimerRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    try {
      if (window.localStorage.getItem(COACH_LS_KEY)) return undefined;
    } catch (_) { return undefined; }
    const showT = setTimeout(() => setShowCoach(true), 700);
    coachTimerRef.current = setTimeout(() => {
      setShowCoach(false);
      try { window.localStorage.setItem(COACH_LS_KEY, "1"); } catch (_) {}
    }, 5200);
    return () => {
      clearTimeout(showT);
      if (coachTimerRef.current) clearTimeout(coachTimerRef.current);
    };
  }, []);

  const handle = (e) => {
    try { e.stopPropagation(); e.preventDefault(); } catch (_) {}
    if (showCoach) {
      setShowCoach(false);
      try { window.localStorage.setItem(COACH_LS_KEY, "1"); } catch (_) {}
    }
    onClick && onClick();
  };

  return (
    <div className={`relative inline-flex ${className}`}>
      {showCoach && (
        <div
          data-testid="background-play-coach"
          className="absolute -top-9 left-1/2 -translate-x-1/2 px-2.5 py-1 text-terracotta hover:text-terracotta/80 text-[10px] tracking-[0.18em] uppercase font-bold shadow-xl pointer-events-none whitespace-nowrap animate-bounce"
        >
          ↓ Keep listening while you wander
        </div>
      )}
      <button
        type="button"
        onClick={handle}
        data-testid={testid || "background-play-pill"}
        aria-label="Mini Player — keep audio playing while you browse"
        title="Mini Player — shrink into a floating window, keeps audio playing while you browse other rooms"
        style={{ touchAction: "manipulation" }}
        className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-terracotta text-cream hover:bg-terracotta/85 text-[10px] font-bold tracking-[0.18em] uppercase shadow-md transition-colors ${showCoach ? "animate-pulse" : ""}`}
      >
        <Headphones size={12} weight="fill" />
        <span>Mini Player</span>
      </button>
    </div>
  );
};

export default BackgroundPlayPill;
